// Cohort Course Types — free live sessions served under /courses
// (e.g. the Agentic AI Masterclass: one 90-minute live session).
// Distinct from the paid `Course` type: scheduled sessions, agenda blocks
// and the post-session bridge have no equivalent fields there.
export interface CohortCourse {
	slug: string;
	// Where the cohort's landing experience lives now that it has no detail
	// page (e.g. the free-session registration page /masterclass). Catalog
	// cards, funnel CTAs, and the paid course bridge link here; the sitemap
	// skips the cohort URL.
	detailRedirect?: string;
	title: string;
	description: string; // hero lead paragraph
	level: 'beginner' | 'intermediate' | 'advanced';
	tags: string[]; // hero badges, e.g. "Free", "Live Session"
	price: 0; // free sessions (kept explicit for JSON-LD offers)
	learn: string[]; // "What you'll learn" checklist
	agenda: AgendaBlock[]; // run-of-show for the session(s)
	labs?: CohortLab[]; // optional take-home labs (multi-session courses)
	highlight?: CohortHighlight; // the page's single feature card (e.g. the live demo moment)
	nextStep?: CohortNextStep; // post-session bridge to a paid offering
	cohort: CohortSchedule;
}

export interface AgendaBlock {
	title: string;
	items: AgendaItem[];
}

export interface AgendaItem {
	title: string;
	duration: string;
}

export interface CohortLab {
	title: string;
	description: string;
	duration: string;
}

export interface CohortHighlight {
	title: string;
	description: string;
}

export interface CohortNextStep {
	label: string; // CTA label, e.g. "Join the 10-Week Masterclass"
	href: string; // internal route
	description: string;
}

export interface CohortSchedule {
	startDate: string; // ISO date of first session, e.g. '2026-11-10'
	timeLabel: string; // e.g. '7:00 PM IST'
	timezone: string; // IANA zone, e.g. 'Asia/Kolkata'
	timezoneOffset: string; // e.g. '+05:30' — for ICS DTSTART conversion
	sessionDays: ('MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU')[]; // ICS weekdays
	cadence: string; // human label, e.g. 'One live session · 90 min'
	sessionLength: string; // e.g. '90 min'
	weeks: number; // session count = weeks × sessionDays.length
}

// Derived stats shown in the hero — computed from data, never duplicated
export interface CohortStats {
	sessions: number;
	agendaItems: number;
	labs: number;
	weeks: number;
}

export function cohortStats(course: CohortCourse): CohortStats {
	return {
		sessions: course.cohort.weeks * course.cohort.sessionDays.length,
		agendaItems: course.agenda.reduce((acc, b) => acc + b.items.length, 0),
		labs: course.labs?.length ?? 0,
		weeks: course.cohort.weeks
	};
}
